import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCrown, 
  faLeaf,
  faArrowRight,
  faTicketAlt,
  faClock,
  faUsers,
  faInfoCircle,
  faEnvelope,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

// Import images
import castleImage from '../../../assets/castle2.png';
import gardensImage from '../../../assets/gardens.png';
import afternoonTeaImage from '../../../assets/tearoom.jpg';
import tearoomBanner from '../../../assets/banner/tearoomBan.webp';

const AfternoonTeaContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const NewsletterBadge = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, #d4af37);
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-align: center;
  margin: 0 auto 2rem;
  max-width: 400px;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const SpecialOfferText = styled.div`
  background: linear-gradient(135deg, #f8f4e6, #faf6ed);
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
  
  h3 {
    color: ${props => props.theme.colors.accent};
    font-size: 1.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }
  
  p {
    color: ${props => props.theme.colors.primary};
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.colors.accent};
  }
`;

const OptionImage = styled.div`
  height: 300px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '${props => props.badge}';
    position: absolute;
    top: 15px;
    right: 15px;
    background: ${props => props.theme.colors.accent};
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const OptionContent = styled.div`
  padding: 2rem;
`;

const OptionTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const OptionDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2rem;
`;

const OptionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const PriceTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '${props => props.label}';
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    background: #d4af37;
    color: white;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    white-space: nowrap;
  }
`;

const BookButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.theme.colors.accent};
  font-size: 1.1rem;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.greyBackground}, #f8f8f8);
  padding: 2rem;
  border-radius: 12px;
  margin-top: 4rem;
  text-align: center;
  border: 1px solid ${props => props.theme.colors.accent};
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const NewsletterAfternoonTea = () => {
  return (
    <AfternoonTeaContainer>
      <ParallaxBanner 
        title="Add Afternoon Tea to Your Visit"
        subtitle="Enhance Your Castle Experience"
        image={tearoomBanner}
      />
      
      <ContentContainer>
        <SectionTitle>Add Afternoon Tea to Your Booking</SectionTitle>
        <IntroText>
          Thank you for booking your visit to Belvoir Castle! Enhance your experience by adding 
          our exquisite Afternoon Tea to your day. Choose the option that suits your booking below.
        </IntroText>
        
        <SpecialOfferText>
          <h3>🫖 Perfect Addition to Your Visit</h3>
          <p>
            Our Afternoon Tea features estate-sourced ingredients and artisanal delicacies in the elegant 
            Aviary Tearoom. Select the option that matches your current booking.
          </p>
        </SpecialOfferText>
        
        <OptionsGrid>
          <OptionCard>
            <OptionImage image={afternoonTeaImage} badge="ADD TO BOOKING" />
            <OptionContent>
              <OptionTitle>
                <FontAwesomeIcon icon={faPlus} />
                Add Afternoon Tea Only
              </OptionTitle>
       
              <OptionDescription>
                Perfect if you already have Castle or Gardens entry tickets! Simply add our exquisite 
                Afternoon Tea service to your existing booking. Enjoy freshly baked scones, delicate 
                sandwiches, and fine teas in our historic Aviary Tearoom.
              </OptionDescription>
              <OptionDetails>
                <DetailItem>
                  <FontAwesomeIcon icon={faClock} />
                  <div>
                    <strong>Times:</strong><br />
                    12:00 - 16:00
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faUsers} />
                  <div>
                    <strong>Duration:</strong><br />
                    Leisurely pace
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faTicketAlt} />
                  <div>
                    <strong>Add-on Price:</strong><br />
                    Adult: £35 each <br /> Child: £15 each
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <div>
                    <strong>Requirement:</strong><br />
                    Entry ticket needed
                  </div>
                </DetailItem>
              </OptionDetails>
              <BookButton 
                href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=802a6f7c-35d8-4e61-bebc-1bb89eff1662" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Add Afternoon Tea <FontAwesomeIcon icon={faArrowRight} />
              </BookButton>
            </OptionContent>
          </OptionCard>

          <OptionCard>
            <OptionImage image={castleImage} badge="COMPLETE PACKAGE" />
            <OptionContent>
              <OptionTitle>
                <FontAwesomeIcon icon={faCrown} />
                Entry Ticket + Afternoon Tea
              </OptionTitle>

              <OptionDescription>
                Don't have entry tickets yet? Choose this option for full Castle & Gardens access 
                plus our exquisite Afternoon Tea service. The complete Belvoir experience in one 
                convenient booking - explore the castle and then enjoy afternoon tea.
              </OptionDescription>
              <OptionDetails>
                <DetailItem>
                  <FontAwesomeIcon icon={faCrown} />
                  <div>
                    <strong>Includes:</strong><br />
                    Castle & Gardens
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faLeaf} />
                  <div>
                    <strong>Plus:</strong><br />
                    Afternoon Tea
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faClock} />
                  <div>
                    <strong>Tea Times:</strong><br />
                    12:00 - 16:00
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faTicketAlt} />
                  <div>
                    <strong>Value:</strong><br />
                    Complete package
                  </div>
                </DetailItem>
              </OptionDetails>
              <BookButton 
                href="https://belvoircastle.com/castle-dining/afternoon-tea-booking" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Book Complete Package <FontAwesomeIcon icon={faArrowRight} />
              </BookButton>
            </OptionContent>
          </OptionCard>
        </OptionsGrid>

        <InfoBox>
          <InfoText>
            <FontAwesomeIcon icon={faInfoCircle} /> Afternoon Tea is served daily from 12:00pm to 4:00pm in our elegant Aviary Tearoom.
            Advance booking is recommended, especially during busy periods.
          </InfoText>
          <InfoText>
            <strong>Enhance your visit - indulge in a truly memorable afternoon tea experience!</strong>
          </InfoText>
        </InfoBox>

        {/* Additional Options */}
        <SectionTitle style={{ marginTop: '4rem' }}>More Dining Options</SectionTitle>
        <IntroText>
          Looking for other dining experiences? Explore our Aviary Tearoom menu with light lunches, 
          beverages, and sweet treats available throughout the day.
        </IntroText>
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <BookButton href="/castle-dining/aviary-tearoom">
            Visit Aviary Tearoom <FontAwesomeIcon icon={faArrowRight} />
          </BookButton>
        </div> 
      </ContentContainer>
      
      <Footer />
    </AfternoonTeaContainer>
  );
};

export default NewsletterAfternoonTea; 